import React, { useEffect, useMemo, useRef, useState } from "react"
import { Container } from "reactstrap"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import TableContainer from "components/Common/TableContainer"
import eye from "../../../assets/images/icons/eye.svg"
import IcdProcess from "../../../assets/images/icd-process.png"
import { Link } from "react-router-dom"
import DeleteConfirmationModal from "modals/deleteConfirmation"
import moment from "moment"
import {
  formatAmount,
  getIcdRequestStatus,
  getTableSortingHeaderIcon,
} from "helpers/common"
import { useDeleteIcdRequestMutation } from "store/api/investorapi/icds.api"
import { usePermission } from "hooks/usePermission"
import { useLazyIcdRequestsWpQuery } from "store/api/wealthpartnerapi/wpicds.api"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"
const defaultFilters = {
  search: "",
  page: 1,
  pageSize: 20,
  timestamp: "",
  createdBy: "All",
}

const ICDRequestsWp = () => {
  const [getResults, { isFetching }] = useLazyIcdRequestsWpQuery()
  const [onDelete, { isLoading: isDeleting }] = useDeleteIcdRequestMutation()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [isToast, setIsToast] = useState(false)
  const deleteRef = useRef(null)
  const { hasPermission } = usePermission()

  //meta title
  document.title = "ICD Investments - Prachay"

  const getList = async () => {
    try {
      const { data } = await getResults(filters).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  useEffect(() => {
    getList()
  }, [filters])

  const onDeleteSuccess = () => {
    setIsToast(false)
    deleteRef.current = null
    setFilters((prev) => ({ ...prev, page: 1, timestamp: moment().unix() }))
  }

  const columns = useMemo(() => [
    {
      header: ({ column }) => getTableSortingHeaderIcon(column, " Request ID"),
      accessorKey: "unique_code",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="code-number">{row?.original.icds.unique_code}</div>
      ),
    },
    {
      header: "Investor Detail",
      accessorKey: "name",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <DefaultAvatar
          avatar_url={row?.original.investor?.avatar_url}
          email={row?.original.investor?.email}
          name={row?.original.investor?.name}
        />
      ),
    },
    {
      header: ({ column }) =>
        getTableSortingHeaderIcon(column, " Fund Availability Date"),
      accessorKey: "fund_requirement_date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <>{moment(row?.original?.fund_requirement_date).format("D MMM YYYY")}</>
      ),
    },
    {
      header: ({ column }) => getTableSortingHeaderIcon(column, " Amount (₹)"),
      accessorKey: "amount",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => <div>₹ {formatAmount(row?.original?.amount)}</div>,
    },
    {
      header: ({ column }) => getTableSortingHeaderIcon(column, " Interest"),
      accessorKey: "interest_rate",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="">{row?.original?.interest_rate} %</div>
      ),
    },
    {
      header: ({ column }) =>
        getTableSortingHeaderIcon(column, "Date of Repayment"),
      accessorKey: "repayment_date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="">
          {row?.original?.repayment_date
            ? moment(row?.original?.repayment_date).format("D MMM YYYY")
            : ""}
        </div>
      ),
    },
    {
      header: ({ column }) => getTableSortingHeaderIcon(column, "Created By"),
      accessorKey: "handled_by",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="">
          {row?.original?.handled_by === "INVESTOR"
            ? "Investor"
            : "Wealth Partner"}
        </div>
      ),
    },
    {
      header: () => <div className="flex items-center gap-2">Status</div>,
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="code-status">
          {getIcdRequestStatus(row?.original?.status)}
        </div>
      ),
    },
    {
      id: "action",
      header: "Actions",
      cell: ({ row }) => (
        <div className="d-flex justify-content-center align-items-center gap-3">
          <div className="action-view">
            <Link
              to={`/investment-management/fund-requests/view/${row?.original.id}`}
            >
              <img src={eye} alt="" />
            </Link>
          </div>
        </div>
      ),
    },
  ])

  const HeaderComponent = () => (
    <>
      <img src={IcdProcess} className="img-fluid mb-2" alt="" />
    </>
  )

  const FilterComponents = () => (
    <>
      <div className="">
        <select
          className="form-select"
          style={{
            minWidth: "200px",
          }}
          value={filters.createdBy}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              page: 1,
              createdBy: e.target.value,
            }))
          }
        >
          {["All", "Investor Created", "WP Created"].map((item, index) => (
            <option key={`investorStatus_${index}`} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>
    </>
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={"List of Investments"} breadcrumbItem={"ICDs"} />
          <TableContainer
            isLoading={isFetching}
            count={count}
            filters={filters}
            setFilters={setFilters}
            FilterComponents={FilterComponents}
            HeaderComponent={HeaderComponent}
            columns={columns}
            data={data}
            isGlobalFilter={true}
            isCustomPageSize={true}
            isPagination={true}
            pagination="pagination"
            paginationWrapper="dataTables_paginate paging_simple_numbers"
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        </Container>

        {isToast && deleteRef && (
          <DeleteConfirmationModal
            open={isToast}
            id={deleteRef.current.id}
            setOpen={setIsToast}
            isLoading={isDeleting}
            onClose={() => {
              setIsToast(false)
              deleteRef.current = null
            }}
            title={"Delete ICD"}
            description="Are you sure you want to delete this icd?"
            onDelete={onDelete}
            onSuccess={onDeleteSuccess}
          />
        )}
      </div>
    </React.Fragment>
  )
}

export default ICDRequestsWp
